import { SolutionPage } from "@/components/shared/SolutionPage";
import { MissedCallTextback } from "@/components/shared/MissedCallTextback";
import { MessageSquare, Calendar, Network, TrendingUp } from 'lucide-react';
import { Link } from "react-router-dom";
import { ServiceFAQ } from "@/components/shared/ServiceFAQ";

/**
 * Capability spoke - "Missed-Call Text-Back".
 * Single-intent page targeting "missed call text back" search demand and its
 * long-tail (dealer/trades). Hub is /platform; deep-links up to /platform and
 * across to /solutions/crm-automation + /platform. Outcome-framed, not a
 * feature dump. First of the capability-spoke tier - mirror this for the next.
 */
const MissedCallTextBack = () => {
  return (
    <SolutionPage
      breadcrumbTitle="Missed-Call Text-Back"
      seo={{
        title: "Missed-Call Text-Back for Dealers & Trades | Frayze",
        description: "Every missed call is a lost sale. Frayze texts the caller back in seconds, answers, and books the job, so you never lose a lead to a ringing phone again.",
        keywords: "missed call text back, missed call text-back, auto text back software, speed to lead, missed call automation, text back for dealerships, missed call text back for contractors",
        canonical: "https://frayze.ca/platform/missed-call-text-back/"
      }}
      hero={{
        badge: "Capability: Conversations",
        badgeColor: "text-teal-700",
        title: "Never Lose a Lead to a Missed Call.",
        description: "When you can't pick up, Frayze texts the caller back in seconds, answers their question and books them in. No missed call, no lost sale, no one touching a phone.",
        buttonColor: "bg-teal-700 hover:bg-teal-800",
        gradientParams: "from-teal-500/5 via-background to-background",
        primaryCta: { text: "Turn On Text-Back", link: "/book-consultation/?tier=foundation" },
        secondaryCta: { text: "See Pricing", link: "/pricing/" },
        visual: <MissedCallTextback />
      }}
      offRamp={{
        label: "Take it further.",
        message: "Text-back catches the calls you miss. Frayze Reception makes sure you never miss one: a managed AI receptionist that answers 24/7, handles the question, and books the job.",
        ctaText: "Meet Frayze Reception",
        ctaLink: "/platform/ai-receptionist/",
      }}
      problem={{
        title: "A Ringing Phone Nobody Answers Is a Sale Walking Away",
        description: (
          <>
            The call comes in mid-test-drive, on a roof, or under a sink. It goes to voicemail, and the caller doesn't leave one. They just dial the next name on Google.
            <br /><br />
            <strong>You never even knew you lost them. The industry research is brutal:</strong>
          </>
        ),
        gridCols: 3,
        cards: [
          { value: "62%", label: "Of calls to local businesses go unanswered at peak times" },
          { value: "85%", label: "Of people whose call isn't answered never call back" },
          { value: "78%", label: "Of customers buy from the business that responds first" }
        ]
      }}
      systemModel={{
        title: "How Missed-Call Text-Back Works",
        description: "The second a call goes unanswered, the system takes over.",
        variant: "steps",
        connectionColor: "bg-gradient-to-r from-teal-500/20 via-teal-500/50 to-teal-500/20",
        items: [
          { step: "1", title: "Ring, No Answer", description: "A call comes in and isn't picked up within the window you set." },
          { step: "2", title: "Instant Text Fires", description: "In seconds the caller gets a branded SMS . 'Sorry we missed you, how can we help?'" },
          { step: "3", title: "Two-Way Conversation", description: "They reply by text. Your team, or the AI, answers from one inbox." },
          { step: "4", title: "Booked", description: "The lead is captured in your CRM and routed to a booking, quote, or the right rep." }
        ]
      }}
      layers={{
        title: "What Comes With Text-Back",
        description: "It's one piece of the Conversations layer: built and managed for you, not a bolt-on gadget.",
        theme: "teal",
        items: [
          {
            title: "The Text-Back Engine",
            icon: MessageSquare,
            items: [
              "Fires on every unanswered call",
              "Branded, customizable first message",
              "Business-hours and after-hours logic"
            ]
          },
          {
            title: "What Happens Next",
            icon: Calendar,
            items: [
              "Two-way SMS from one unified inbox",
              "Auto-capture into your CRM",
              "Hand-off to booking or a live rep"
            ]
          }
        ]
      }}
      interdependence={{
        title: "Text-Back Is the First Domino",
        description: (
          <>
            Catching the call is step one. It feeds straight into the <Link to="/solutions/crm-automation/" className="font-bold underline decoration-teal-500/50 hover:text-teal-600 transition-colors">CRM that stores and works the lead</Link>, and it hands off to <Link to="/platform/" className="font-bold underline decoration-indigo-500/50 hover:text-indigo-600 transition-colors">the AI that can answer and book</Link> without your team lifting a finger.
            <br /><br />
            See where it sits in the wider <Link to="/platform/" className="font-bold underline decoration-teal-500/50 hover:text-teal-600 transition-colors">managed platform</Link>.
          </>
        ),
        icon: Network,
        quote: "Catching the call is step one. Frayze runs everything after it."
      }}
      useCases={{
        title: "Built for High-Ticket, Phone-Driven Businesses",
        items: [
          { title: "Car Dealerships", description: "Sales and service lines ring during test drives and busy Saturdays: every missed caller gets texted back before they reach the next dealer.", link: "/automotive/" },
          { title: "Trades & Contractors", description: "You're on a roof or under a sink. The system answers for you and books the estimate.", link: "/industries/trades/" },
          { title: "Any Missed Call = Real Money", description: "When one lead is worth four figures, the call you never knew about is the most expensive kind.", link: "/solutions/crm-automation/" }
        ]
      }}
      socialProof={{
        title: "Proven on real dealership phone lines",
        quote: "Before Frayze, Denis ran the lead engine at Need A Car across six rooftops: every store line on instant text-back. That operator playbook is what we install for you.",
        icon: TrendingUp,
        ctaText: "See the Need A Car story",
        ctaLink: "/case-studies/need-a-car-automotive-growth/"
      }}
      cta={{
        title: "Stop Letting the Phone Cost You Sales",
        description: "Turn on missed-call text-back and never lose a lead to a ringing phone again.",
        primaryCta: { text: "Turn On Text-Back", link: "/book-consultation/?tier=foundation" },
        secondaryCta: { text: "Get Your Free Missed-Call Audit", link: "/digital-audit/" },
        color: "bg-teal-700 hover:bg-teal-800"
      }}
      customSection={
        <div className="space-y-24 pb-24">
          <ServiceFAQ
            title="Missed-Call Text-Back: Common Questions"
            description="Straight answers on how the text-back works and what it costs you to keep missing calls."
            items={[
              {
                question: "What is missed-call text-back?",
                answer: "Missed-call text-back automatically sends a text to anyone whose call to your business goes unanswered. Instead of losing them to voicemail, the caller gets an instant SMS, usually within seconds, that starts a conversation and lets you book them without phone tag."
              },
              {
                question: "How fast does the text go out?",
                answer: "Within seconds of the call ending. Speed is the whole point: the business that responds first wins the majority of leads, and most people will not call back if their first call is not answered."
              },
              {
                question: "Does it work after hours?",
                answer: "Yes. You set the rules. Text-back can fire 24/7 or only outside business hours, with a different message for each, so a caller at 9 PM still gets answered and booked for the morning."
              },
              {
                question: "Will the texts come from my business number?",
                answer: "Yes. Messages are branded with your business name and sent from your Frayze line, so the caller knows who is replying. Replies land in one inbox your team manages from desktop or the mobile app."
              },
              {
                question: "Can AI answer the replies, or just send the first text?",
                answer: "Both. The first text-back fires automatically. From there your team can reply, or you can turn on Frayze Conversation AI to answer questions and book the appointment on its own."
              },
              {
                question: "Is this only for dealerships and trades?",
                answer: "No. It fits any business where a missed call is a lost sale: dealerships, contractors, clinics, salons, law firms. It is most valuable when each lead is worth hundreds or thousands of dollars."
              }
            ]}
          />
        </div>
      }
    />
  );
};

export default MissedCallTextBack;
